'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import useSWRInfinite from 'swr/infinite';
import { trackGa4ItemListView, trackGa4ItemSelection } from '@v2/analytics/ga4';
import {
  getTeamCalendar,
  TeamCalendarGame,
  TeamCalendarQuery,
  TeamCalendarResponse,
  TeamScheduleQuery,
} from '@v2/services/team-schedule';
import { Button } from '@v2/ui/Button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@v2/ui/Drawer';
import { FilterLines } from '@v2/ui/Icon/General';
import { List } from '@v2/ui/Icon/Layout/Layout';
import { CalendarIcon } from '@v2/ui/Icon/Time';
import { cn } from '@v2/utils/cn';
import { EventsListSkeleton } from '../components/EventsList';
import {
  HomeAwayEventsTab,
  HomeAwayEventsTypes,
} from '../components/HomeAwayEventsTab';
import { OpponentsDropdownFilter } from '../components/OpponentsDropdownFilter';
import { ShowMoreButton } from '../components/ShowMoreButton';
import {
  TeamScheduleCalendar,
  TeamScheduleList,
} from '../components/TeamSchedule';
import {
  getDefaultTeamCalendarRange,
  getTeamCalendarPageRange,
  mergeTeamCalendarPages,
} from '../util/teamSchedule';
import { DateRangePicker } from './DateRangePicker';

// Popups inside the Filters sheet portal outside it; tapping them must not close the sheet.
const keepOpenForNestedPopup = (event: {
  target: EventTarget | null;
  preventDefault: () => void;
}) => {
  if (
    (event.target as Element | null)?.closest?.(
      '[data-radix-popper-content-wrapper]'
    )
  ) {
    event.preventDefault();
  }
};

const getOpponentId = (value?: string | null) => {
  const opponentId = Number(value);
  return Number.isSafeInteger(opponentId) && opponentId > 0
    ? opponentId
    : undefined;
};

export function TeamCalendarWrapper({
  league,
  teamSlug,
  teamName,
  initialCalendar,
  confidence,
}: TeamScheduleQuery & {
  teamName: string;
  initialCalendar?: TeamCalendarResponse;
  confidence?: ReactNode;
}) {
  const searchParams = useSearchParams();
  const opponentIdParam = searchParams?.get('opponentId');
  const [today] = useState(
    () => initialCalendar?.startDate ?? format(new Date(), 'yyyy-MM-dd')
  );
  const [dateFilter, setDateFilter] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [homeAway, setHomeAway] = useState(HomeAwayEventsTypes.ALL);
  const [opponentId, setOpponentId] = useState(() =>
    getOpponentId(opponentIdParam)
  );
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const trackedLists = useRef(new Set<string>());

  useEffect(() => {
    setOpponentId(getOpponentId(opponentIdParam));
  }, [opponentIdParam]);

  const startDate = dateFilter.from
    ? format(dateFilter.from, 'yyyy-MM-dd')
    : today;
  const range = {
    startDate,
    endDate: dateFilter.to
      ? format(dateFilter.to, 'yyyy-MM-dd')
      : getDefaultTeamCalendarRange(startDate).endDate,
  };
  const allowMore = !dateFilter.to;
  const activeFilterCount = [
    dateFilter.from,
    opponentId,
    homeAway !== HomeAwayEventsTypes.ALL,
  ].filter(Boolean).length;
  const hasFilters = activeFilterCount > 0;
  const resetFilters = () => {
    setDateFilter({ from: undefined, to: undefined });
    setHomeAway(HomeAwayEventsTypes.ALL);
    setOpponentId(undefined);
  };
  const validRange = getTeamCalendarPageRange(range, 0, allowMore) !== null;
  const { data, error, size, setSize, isValidating, mutate } =
    useSWRInfinite<TeamCalendarResponse>(
      (pageIndex) => {
        const pageRange = getTeamCalendarPageRange(range, pageIndex, allowMore);
        if (!pageRange) return null;
        const query: TeamCalendarQuery = {
          league,
          teamSlug,
          ...pageRange,
          onlyHomeGames:
            homeAway === HomeAwayEventsTypes.ALL
              ? undefined
              : homeAway === HomeAwayEventsTypes.HOME,
          opponentId,
        };
        return ['team-calendar', query, allowMore] as const;
      },
      ([, query]: readonly [string, TeamCalendarQuery, boolean]) =>
        getTeamCalendar(query),
      {
        fallbackData:
          initialCalendar &&
          !dateFilter.from &&
          homeAway === HomeAwayEventsTypes.ALL &&
          opponentId === undefined
            ? [initialCalendar]
            : undefined,
        revalidateOnMount: true,
        revalidateFirstPage: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        // The API returns 503 on a transient upstream (Tevo) failure; auto-retry so a blip heals without a click.
        errorRetryCount: 3,
        errorRetryInterval: 1000,
      }
    );

  const { games, opponents } = useMemo(
    () => mergeTeamCalendarPages(data ?? []),
    [data]
  );
  const pages = data ?? [];
  const firstPage = pages[0];
  const lastPage = pages[pages.length - 1];
  const opponentOptions = [
    { teamId: 'all', team: 'All opponents' },
    ...opponents.map((opponent) => ({
      teamId: opponent.id,
      team: opponent.name,
    })),
  ];
  const selectedOpponent = opponentId
    ? (opponentOptions.find((opponent) => opponent.teamId === opponentId) ?? {
        teamId: opponentId,
        team: 'Selected opponent',
      })
    : undefined;
  const isLoadingMore = isValidating || (!error && size > pages.length);
  const hasMore =
    getTeamCalendarPageRange(range, pages.length, allowMore) !== null;
  // Off-season windows can be empty; load the next month until games appear (bounded by the 12-month range).
  const isSkippingEmptyMonths = !!data && !games.length && !error && hasMore;
  const itemListId = `team_schedule_${league}_${teamSlug}`;

  useEffect(() => {
    // Wait for in-flight revalidation; an empty server fallback isn't cached yet.
    if (isSkippingEmptyMonths && !isValidating && size === pages.length)
      void setSize(size + 1);
  }, [isSkippingEmptyMonths, isValidating, size, pages.length, setSize]);

  useEffect(() => {
    if (!games.length) return;
    const listKey = `${itemListId}:${games.map((game) => game.eventId).join(',')}`;
    if (trackedLists.current.has(listKey)) return;
    trackedLists.current.add(listKey);
    trackGa4ItemListView({
      itemListId,
      itemListName: 'team_games',
      items: games.map((game, index) => ({
        item_id: String(game.eventId),
        item_name: game.name,
        item_category: league,
        index,
      })),
    });
  }, [games, itemListId, league]);

  const onSelect = (game: TeamCalendarGame) => {
    trackGa4ItemSelection({
      itemListId,
      itemListName: 'team_games',
      item: {
        item_id: String(game.eventId),
        item_name: game.name,
        item_category: league,
        index: games.findIndex((item) => item.eventId === game.eventId),
      },
    });
  };

  // Rendered in the desktop row and the mobile Filters panel.
  const homeAwayFilter = (
    <HomeAwayEventsTab
      variant="responsive"
      value={homeAway}
      onTabChange={setHomeAway}
    />
  );
  const dateRangeFilter = (
    <DateRangePicker
      selectedDateFilter={dateFilter}
      onDateChange={setDateFilter}
      defaultLabel="All dates"
      compactMobile
      singleMonth
    />
  );
  const opponentFilter = (
    <OpponentsDropdownFilter
      teams={opponentOptions}
      selectedValue={selectedOpponent}
      onOpponentChange={(opponent) =>
        setOpponentId(
          typeof opponent.teamId === 'number' ? opponent.teamId : undefined
        )
      }
      onSearch={() => undefined}
      defaultLabel="Opponents"
      compactMobile
    />
  );

  return (
    <section aria-label={`${teamName} games`} className="min-w-0">
      <h2 className="mb-2 text-xl font-bold leading-8 text-gray-800 md:text-2xl">
        All {firstPage?.team.name ?? teamName} Games
      </h2>
      <p className="mb-5 text-sm text-gray-600">
        Browse home and away games and build your trip with tickets, hotel and
        optional flights.
      </p>
      <div
        role="group"
        aria-label="Game filters"
        className="mb-5 flex items-stretch gap-2 md:flex-wrap md:items-center"
      >
        <Drawer
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          handleOnly
          autoFocus
          shouldScaleBackground={false}
        >
          <DrawerTrigger asChild>
            <Button
              variant="secondary"
              aria-label={
                hasFilters ? `Filters, ${activeFilterCount} applied` : 'Filters'
              }
              className="min-w-0 flex-1 gap-2 md:hidden"
            >
              <FilterLines size="20" className="shrink-0" />
              Filters
              {hasFilters && (
                <span className="flex size-5 items-center justify-center rounded-full bg-gray-800 text-xs font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </DrawerTrigger>
          <DrawerContent
            onPointerDownOutside={keepOpenForNestedPopup}
            onInteractOutside={keepOpenForNestedPopup}
            className="max-h-[90svh] rounded-t-[24px] border-0 bg-white [&>div:first-child]:mt-3 [&>div:first-child]:h-1 [&>div:first-child]:w-10 [&>div:first-child]:shrink-0 [&>div:first-child]:rounded-2xl [&>div:first-child]:bg-gray-300 [&>div:first-child]:opacity-70"
          >
            <DrawerHeader className="p-4 text-center sm:text-center">
              <DrawerTitle className="text-xl font-bold leading-7 tracking-normal text-gray-800">
                Filters
              </DrawerTitle>
              <DrawerDescription className="sr-only">
                Filter {teamName} games by home or away, dates and opponent.
              </DrawerDescription>
            </DrawerHeader>
            <div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-4 pb-4">
              {(
                [
                  ['Home & Away', homeAwayFilter],
                  ['Dates', dateRangeFilter],
                  ['Opponent', opponentFilter],
                ] as const
              ).map(([label, control]) => (
                <div key={label} className="grid gap-2">
                  <p className="text-sm font-semibold text-gray-800">{label}</p>
                  {control}
                </div>
              ))}
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-gray-200 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-4">
              <Button
                variant="secondary"
                disabled={!hasFilters}
                onClick={resetFilters}
              >
                Reset
              </Button>
              <DrawerClose asChild>
                <Button>Show games</Button>
              </DrawerClose>
            </div>
          </DrawerContent>
        </Drawer>
        <div className="hidden md:contents">
          {homeAwayFilter}
          {dateRangeFilter}
          <div className="min-w-0">{opponentFilter}</div>
        </div>
        <div
          role="group"
          aria-label="Games view"
          className="flex gap-1 rounded-lg border border-gray-200 p-1"
        >
          {(['list', 'calendar'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-label={`${mode === 'list' ? 'List' : 'Calendar'} view`}
              aria-pressed={view === mode}
              onClick={() => setView(mode)}
              className={`flex size-11 items-center justify-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 ${view === mode ? 'bg-gray-800 text-white' : 'text-gray-500'}`}
            >
              {mode === 'list' ? (
                <List size="20" />
              ) : (
                <CalendarIcon size="20" />
              )}
            </button>
          ))}
        </div>
        {hasFilters && (
          <Button
            variant="tertiary"
            className="hidden md:inline-flex"
            onClick={resetFilters}
          >
            Reset filters
          </Button>
        )}
      </div>
      <div
        className={cn(
          'grid min-w-0 items-start gap-10 lg:gap-12',
          view === 'list' &&
            confidence &&
            'lg:grid-cols-[minmax(0,792fr)_minmax(0,440fr)]'
        )}
      >
        <div className="min-w-0">
          {!validRange ? (
            <p role="alert" className="py-4 text-sm text-gray-600">
              Choose an end date on or after the start date, within 12 calendar
              months.
            </p>
          ) : (!data && !error) || isSkippingEmptyMonths ? (
            <div role="status" aria-label="Loading team games">
              <EventsListSkeleton numberOfLines={5} />
            </div>
          ) : (
            <>
              {!games.length && !error ? (
                <p
                  role="status"
                  className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600"
                >
                  {hasFilters
                    ? 'No games found for these dates and filters.'
                    : 'No upcoming games scheduled in the next 12 months.'}
                </p>
              ) : firstPage && lastPage ? (
                view === 'calendar' ? (
                  <TeamScheduleCalendar
                    key={`${homeAway}:${opponentId ?? 'all'}:${range.startDate}:${range.endDate}`}
                    games={games}
                    range={{
                      startDate: firstPage.startDate,
                      endDate: lastPage.endDate,
                    }}
                    onSelect={onSelect}
                  />
                ) : (
                  <TeamScheduleList games={games} onSelect={onSelect} />
                )
              ) : null}
              {error && (
                <div
                  role="alert"
                  className="my-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600"
                >
                  <p>
                    We couldn&apos;t load{' '}
                    {pages.length ? 'more games' : 'team games'}. Please try
                    again.
                  </p>
                  {!pages.length && (
                    <Button
                      variant="secondary"
                      className="mt-3"
                      onClick={() => void mutate()}
                    >
                      Try games again
                    </Button>
                  )}
                </div>
              )}
              {!!pages.length && hasMore && (
                <ShowMoreButton
                  isLoading={isLoadingMore}
                  label={error ? 'Try more games again' : 'Show More'}
                  onShowMoreClick={() => void setSize(pages.length + 1)}
                />
              )}
            </>
          )}
        </div>
        {confidence &&
          (view === 'list' ? (
            confidence
          ) : (
            <div className="lg:hidden">{confidence}</div>
          ))}
      </div>
    </section>
  );
}
